// M4-2: the player-centric trade workbench.
//
// The old three-step wizard (pick opponent -> add assets -> review) is retired.
// The new flow is target-first: the user taps "I want him" on another team's
// player (Player page), which seeds this page via the existing `tradeFor`
// action (a trade record with the target on the other side). The workbench then
// shows the AI's asking price up front and lets the user assemble a counter
// package with a live value balance beam and a one-tap auto-fill, submits it
// through the M4-1 patience state machine (accept / counter / reject), and on
// accept executes through a server-validated confirmation path (which reuses
// core propose without rerolling the AI or bypassing league rules), so the N4-2
// success toast / highlight-card and the celebration/news pipelines still fire.
//
// Four render states, all direction-agnostic so a v2 reverse ("sell") flow can
// reuse the same pieces:
//   empty    — no target: prompt + browse entries (leaders / team rosters)
//   quote    — the target + the AI's asking price (GM line + assets + beam)
//   assembly — the user's package builder (add/remove, auto-fill, live beam)
//   result   — accept / counter / reject after a submit
//
// The trade RECORD stays the single source of truth for the assembled package
// (user give = props.userPids/userDpids, other receive = [target]); assembly
// actions go through the existing updateTrade RPC so props.summary stays exact
// and confirmation revalidates that same stored record. previewTradeOffer
// supplies the live beam without touching patience; evaluateTradeOffer is the
// submit only.

import { useEffect, useState } from "react";
import clsx from "clsx";
import { PHASE } from "../../../../../common/constants.ts";
import type { TradeTeams, View } from "../../../../../common/types.ts";
import AvailabilityCard from "../../components/AvailabilityCard.tsx";
import { DraftPicksSheet } from "../../components/DraftPicksSheet.tsx";
import { PlayerNameLabels } from "../../components/PlayerNameLabels.tsx";
import { MobileP3EventFeedback } from "../../mobile/MobileP3EventFeedback.tsx";
import { MobileContentHero, MobileStatRow } from "../../mobile/mobilePrimitives.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { helpers } from "../../util/helpers.ts";
import i18n from "../../util/i18n.ts";
import { formatSeasonRecord } from "../../util/seasonRecord.ts";
import { logEvent } from "../../util/logEvent.ts";
import { getTradeSuccessToastText } from "../../util/successToast.ts";
import { getTradeHighlightCard, isBigTrade } from "../../util/feedbackHeadline.ts";
import { emitHighlightCard } from "../../util/highlightCard.ts";
import { createMobileCrossTabHref } from "../../util/mobileCrossTabLink.ts";
import { getPhaseRoute } from "../../util/phaseNavigation.ts";
import { PHASE_TEXT } from "../../util/phaseText.ts";
import { PositionText } from "../../util/positionNames.tsx";
import { translateTeamName, translateTeamRegion } from "../../util/teamNames.ts";
import { toWorker } from "../../util/toWorker.ts";
import { getRecommendedTradeOpponents } from "./tradeFlowState.ts";
import { MarketRatingStatusMetric } from "../MarketRatingStatusMetric.tsx";

// --- Typed RPC wrappers ------------------------------------------------------
// toWorker forwards exactly one data param, so the M4-1 endpoints take a single
// object. These module-level wrappers also give us the fully-expanded result
// types for component state without cross-importing the worker API type.

type AssetArg = { pids: number[]; dpids: number[] };
type OfferArg = { pid: number; give: AssetArg; receive: AssetArg };

const rpcQuote = (pid: number) => toWorker("main", "getTradeQuote", pid);
const rpcPreview = (arg: OfferArg) => toWorker("main", "previewTradeOffer", arg);
const rpcSuggest = (arg: { pid: number; pids: number[]; dpids: number[] }) =>
  toWorker("main", "suggestTradePackage", arg);
const rpcEvaluate = (arg: OfferArg) => toWorker("main", "evaluateTradeOffer", arg);
const rpcConfirm = (arg: OfferArg) => toWorker("main", "confirmTradeOffer", arg);

type QuoteOk = Extract<Awaited<ReturnType<typeof rpcQuote>>, { ok: true }>;
type PreviewOk = Extract<Awaited<ReturnType<typeof rpcPreview>>, { ok: true }>;
type EvaluateResult = Awaited<ReturnType<typeof rpcEvaluate>>;
type ConfirmResult = Awaited<ReturnType<typeof rpcConfirm>>;
type ExecutionError = Extract<ConfirmResult, { ok: false }>["reasonHint"];
type WorkbenchAsset = QuoteOk["ask"]["give"][number];
type Beam = {
  giveValue: number;
  receiveValue: number;
  gap: number;
  willAccept: boolean;
};

// Server-issued untradable reasons arrive as English strings; translate the two
// known shapes (moved here from the retired AssetList so the messages stay
// localized in the add-your-players list).
const translateUntradableMessage = (message: string | undefined): string | undefined => {
  if (!message) {
    return message;
  }
  if (message === "Cannot trade expired contracts") {
    return i18n.t("trade.cannotTradeExpiredContracts", {
      ns: "ui",
      defaultValue: "Cannot trade expired contracts",
    });
  }
  const recentlyAcquiredMatch =
    /^Cannot trade recently-acquired player for ([0-9]+) more days$/.exec(message);
  if (recentlyAcquiredMatch) {
    return i18n.t("trade.cannotTradeRecentlyAcquiredPlayer", {
      ns: "ui",
      defaultValue: "Cannot trade recently-acquired player for {{days}} more days",
      days: recentlyAcquiredMatch[1],
    });
  }
  return message;
};

const formatValue = (v: number) => (v >= 0 ? `+${v.toFixed(1)}` : v.toFixed(1));

// GM voice for an unattainable ask (quote.attainable === false): the engine
// found NO package of the user's tradable assets the AI would take, so there
// is no asking price to phrase — this honest line replaces it.
const gmUnattainableLine = (targetName: string) =>
  i18n.t("trade.gmUnattainableLine", {
    ns: "ui",
    defaultValue: "You want {{name}}? Nothing you can offer right now would get that done.",
    name: targetName,
  });

// One-line GM voice, composed client-side from the M4-1 `hint` facts. The
// engine only supplies the facts; the sentence is built from i18n templates.
const gmAskLine = (targetName: string, hint: QuoteOk["hint"]) => {
  // Guard: an empty ask means the deal is unattainable — the "about 0 real
  // pieces" sentence must never render, whatever the caller passed.
  if (hint.numAssets === 0) {
    return gmUnattainableLine(targetName);
  }
  const base = i18n.t("trade.gmAskLine", {
    ns: "ui",
    defaultValue: "You want {{name}}? We'll need about {{count}} real piece(s) back.",
    name: targetName,
    count: hint.numAssets,
  });
  const flavor = hint.wantsYoungBigs
    ? i18n.t("trade.gmAskYoungBigs", {
        ns: "ui",
        defaultValue: "We're after a young big man.",
      })
    : hint.wantsPicks
      ? i18n.t("trade.gmAskPicks", {
          ns: "ui",
          defaultValue: "We're after draft picks.",
        })
      : "";
  return flavor ? `${base} ${flavor}` : base;
};

const toneLine = (toneLevel: number) => {
  const clamped = Math.max(0, Math.min(3, toneLevel));
  const defaults: Record<number, string> = {
    0: "Close — sweeten it a little and we've got a deal.",
    1: "That offer doesn't work for us. Get serious.",
    2: "I'm running out of patience here.",
    3: "This is my final word on it.",
  };
  return i18n.t(`trade.gmTone${clamped}`, {
    ns: "ui",
    defaultValue: defaults[clamped],
  });
};

const executionErrorText = (reason: ExecutionError) => {
  if (reason === "salary_match") {
    return i18n.t("trade.rejectSalaryMatch", {
      ns: "ui",
      defaultValue:
        "This package breaks salary-matching rules. Adjust the salaries or assets and try again.",
    });
  }
  if (reason === "hard_cap") {
    return i18n.t("trade.rejectHardCap", {
      ns: "ui",
      defaultValue:
        "This package would put a team over the hard cap. Adjust the salaries or assets and try again.",
    });
  }
  if (reason === "phase_closed") {
    return i18n.t("trade.executionPhaseClosed", {
      ns: "ui",
      defaultValue: "The trade window closed before the deal could be completed.",
    });
  }
  if (reason === "offer_changed" || reason === "no_longer_accepted") {
    return i18n.t("trade.executionOfferChanged", {
      ns: "ui",
      defaultValue:
        "The offer changed before confirmation. Review the current package and submit it again.",
    });
  }
  return i18n.t("trade.executionFailed", {
    ns: "ui",
    defaultValue:
      "The trade could not be completed. Your offer is still here—review it and try again.",
  });
};

// --- Balance beam (direction-agnostic, reusable) -----------------------------

const BalanceBeam = ({ beam }: { beam: Beam }) => {
  const max = Math.max(beam.giveValue, beam.receiveValue, 0.0001);
  const givePct = Math.max(4, Math.round((beam.giveValue / max) * 100));
  const receivePct = Math.max(4, Math.round((beam.receiveValue / max) * 100));
  return (
    <div
      className="trade-balance-beam border rounded p-2 mb-3"
      data-trade-balance-beam=""
      data-trade-salary-value-balance=""
    >
      <div className="d-flex justify-content-between small text-body-secondary mb-1">
        <span>{i18n.t("trade.beamGiveLabel", { ns: "ui", defaultValue: "You give" })}</span>
        <span className="fw-semibold">{formatValue(beam.giveValue)}</span>
      </div>
      <div className="progress mb-2" style={{ height: 6 }}>
        <div className="progress-bar bg-danger" style={{ width: `${givePct}%` }} />
      </div>
      <div className="d-flex justify-content-between small text-body-secondary mb-1">
        <span>{i18n.t("trade.beamReceiveLabel", { ns: "ui", defaultValue: "You get" })}</span>
        <span className="fw-semibold">{formatValue(beam.receiveValue)}</span>
      </div>
      <div className="progress mb-2" style={{ height: 6 }}>
        <div className="progress-bar bg-success" style={{ width: `${receivePct}%` }} />
      </div>
      <div className={clsx("small fw-semibold", beam.willAccept ? "text-success" : "text-warning")}>
        {beam.willAccept
          ? i18n.t("trade.beamAccept", {
              ns: "ui",
              defaultValue: "They'll take this deal",
            })
          : i18n.t("trade.beamShort", {
              ns: "ui",
              defaultValue: "Not enough yet",
            })}
      </div>
    </div>
  );
};

const AssetRow = ({
  asset,
  counterAdd = false,
  onRemove,
  selected = false,
}: {
  asset: WorkbenchAsset;
  counterAdd?: boolean;
  onRemove?: () => void;
  selected?: boolean;
}) => {
  const meta =
    asset.type === "player"
      ? [
          asset.pos,
          asset.ovr != null
            ? i18n.t("trade.ovrShort", {
                ns: "ui",
                ovr: asset.ovr,
                defaultValue: "{{ovr}} ovr",
              })
            : null,
          asset.age != null
            ? i18n.t("trade.yearsOldShort", {
                ns: "ui",
                age: asset.age,
                defaultValue: "{{age}} yo",
              })
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;
  return (
    <div
      className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom"
      data-trade-asset-row={asset.type}
      data-trade-counter-add-player-pid={
        counterAdd && asset.type === "player" ? asset.pid : undefined
      }
      data-trade-player-pid={selected && asset.type === "player" ? asset.pid : undefined}
    >
      <div className="min-width-0">
        <div className="fw-semibold text-truncate">{asset.name}</div>
        {meta ? <div className="small text-body-secondary">{meta}</div> : null}
      </div>
      <div className="d-flex align-items-center gap-2">
        <span className="small fw-semibold">{formatValue(asset.value)}</span>
        {onRemove ? (
          <button
            aria-label={i18n.t("trade.removeAssetAction", {
              ns: "ui",
              defaultValue: "Remove",
            })}
            className="btn btn-sm btn-link text-danger p-0 px-1"
            onClick={onRemove}
            type="button"
          >
            ✕
          </button>
        ) : null}
      </div>
    </div>
  );
};

const Trade = (props: View<"trade">) => {
  const {
    challengeNoRatings,
    challengeNoTrades,
    gameOver,
    otherTeamsWantToHire,
    lost,
    spectator,
    otherTid,
    otherPids,
    otl,
    phase,
    summary,
    strategy,
    teams,
    tied,
    userDpids,
    userPicks,
    userPids,
    userRoster,
    userTid,
    won,
  } = props;

  useTitleBar({
    title: i18n.t("trade.title", { ns: "ui", defaultValue: "Trade" }),
  });

  // The seeded target is the single player on the other side of the trade
  // record (planted by the `tradeFor` action). v1 keeps the receive side fixed
  // to that one player; the user only assembles the give side.
  const targetPid = otherPids.length > 0 ? otherPids[0]! : undefined;

  const [quote, setQuote] = useState<QuoteOk | null>(null);
  const [preview, setPreview] = useState<PreviewOk | null>(null);
  const [outcome, setOutcome] = useState<EvaluateResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftPicksSheetOpen, setDraftPicksSheetOpen] = useState(false);
  // The accepted state survives the trade record clearing so the post-execution
  // celebration remains visible. Execution errors stay on the result card and
  // never silently drop the user back into assembly.
  const [state, setState] = useState({
    accepted: false,
  });
  const [executionError, setExecutionError] = useState<ExecutionError | null>(null);

  const giveKey = `${userPids.join(",")}|${userDpids.join(",")}`;

  // Load the AI's asking price once per target.
  useEffect(() => {
    if (targetPid === undefined) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const q = await rpcQuote(targetPid);
      if (!cancelled && q.ok) {
        setQuote(q);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetPid]);

  // Recompute the live beam whenever the assembled give package changes. This is
  // the READ-ONLY preview — it never burns patience.
  useEffect(() => {
    if (targetPid === undefined) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const p = await rpcPreview({
        pid: targetPid,
        give: { pids: [...userPids], dpids: [...userDpids] },
        receive: { pids: [targetPid], dpids: [] },
      });
      if (!cancelled && p.ok) {
        setPreview(p);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPid, giveKey]);

  const noTradingAllowed =
    (phase >= PHASE.AFTER_TRADE_DEADLINE && phase <= PHASE.PLAYOFFS) ||
    phase === PHASE.FANTASY_DRAFT ||
    phase === PHASE.EXPANSION_DRAFT ||
    gameOver ||
    otherTeamsWantToHire ||
    spectator ||
    challengeNoTrades;

  const marketHomeHref = helpers.leagueUrl(["market"]);

  const buildTeams = (pids: number[], dpids: number[]): TradeTeams => [
    {
      tid: userTid,
      pids,
      pidsExcluded: [],
      dpids,
      dpidsExcluded: [],
    },
    {
      tid: otherTid,
      pids: targetPid !== undefined ? [targetPid] : [],
      pidsExcluded: [],
      dpids: [],
      dpidsExcluded: [],
    },
  ];

  const resetResult = () => {
    setOutcome(null);
    setExecutionError(null);
  };

  const handleToggleGive = async (playerOrPick: "player" | "pick", id: number) => {
    resetResult();
    const pids = [...userPids];
    const dpids = [...userDpids];
    if (playerOrPick === "player") {
      const i = pids.indexOf(id);
      if (i >= 0) {
        pids.splice(i, 1);
      } else {
        pids.push(id);
      }
    } else {
      const i = dpids.indexOf(id);
      if (i >= 0) {
        dpids.splice(i, 1);
      } else {
        dpids.push(id);
      }
    }
    await toWorker("main", "updateTrade", buildTeams(pids, dpids));
  };

  const handleAutoFill = async () => {
    if (targetPid === undefined) {
      return;
    }
    resetResult();
    setBusy(true);
    try {
      const s = await rpcSuggest({
        pid: targetPid,
        pids: [...userPids],
        dpids: [...userDpids],
      });
      if (s.ok) {
        // willAccept:false means the top-up FAILED (makeItWork found no
        // acceptable package even maxing out tradable assets — see
        // workbench.ts). Never apply an unchanged package silently: surface
        // the failure as an explicit toast instead.
        if (!s.willAccept) {
          logEvent({
            type: "error",
            text: i18n.t("trade.autoFillUnattainableReason", {
              ns: "ui",
              defaultValue: "Auto-fill can't reach their price with your current assets",
            }),
            saveToDb: false,
          });
          return;
        }
        const pids = s.suggested.give
          .filter((a) => a.type === "player")
          .map((a) => a.pid!) as number[];
        const dpids = s.suggested.give
          .filter((a) => a.type === "pick")
          .map((a) => a.dpid!) as number[];
        await toWorker("main", "updateTrade", buildTeams(pids, dpids));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (targetPid === undefined) {
      return;
    }
    setExecutionError(null);
    setBusy(true);
    try {
      const result = await rpcEvaluate({
        pid: targetPid,
        give: { pids: [...userPids], dpids: [...userDpids] },
        receive: { pids: [targetPid], dpids: [] },
      });
      setOutcome(result);
    } finally {
      setBusy(false);
    }
  };

  // Confirm through the workbench-specific server path. It revalidates the
  // stored package, phase, salary rules, and the same deterministic value key
  // that produced the accept result before it executes through core propose.
  // This avoids both a second random AI decision and God Mode's rule bypass.
  const executeTrade = async (pids: number[] = [...userPids], dpids: number[] = [...userDpids]) => {
    if (targetPid === undefined) {
      return;
    }
    setExecutionError(null);
    setBusy(true);
    try {
      const result = await rpcConfirm({
        pid: targetPid,
        give: { pids, dpids },
        receive: { pids: [targetPid], dpids: [] },
      });
      if (!result.ok) {
        setExecutionError(result.reasonHint);
        return;
      }

      const otherTeam = teams.find((t) => t.tid === otherTid);
      const partnerName = otherTeam
        ? `${translateTeamRegion(otherTeam.region)} ${translateTeamName(otherTeam.name)}`
        : i18n.t("trade.otherTeam", { ns: "ui", defaultValue: "Other team" });
      const acquiredNames = summary.teams[1].trade.map((p) => p.name);
      const headline = isBigTrade({
        userOvrBefore: summary.teams[0].ovrBefore,
        userOvrAfter: summary.teams[0].ovrAfter,
      });
      if (headline) {
        emitHighlightCard(
          getTradeHighlightCard({
            partnerName,
            acquiredNames,
            url: helpers.leagueUrl(["roster"]),
          }),
        );
      } else {
        const successText = getTradeSuccessToastText({
          accepted: true,
          partnerName,
          acquiredNames,
        });
        if (successText !== undefined) {
          logEvent({ type: "success", text: successText, saveToDb: false });
        }
      }
      setOutcome(null);
      setState({ accepted: true });
    } catch {
      setExecutionError("execution_failed");
    } finally {
      setBusy(false);
    }
  };

  const acceptCounter = async () => {
    if (outcome?.outcome !== "counter" || targetPid === undefined) {
      return;
    }
    const pids = outcome.counter.give
      .filter((a) => a.type === "player")
      .map((a) => a.pid!) as number[];
    const dpids = outcome.counter.give
      .filter((a) => a.type === "pick")
      .map((a) => a.dpid!) as number[];
    await toWorker("main", "updateTrade", buildTeams(pids, dpids));
    await executeTrade(pids, dpids);
  };

  const walkAway = async () => {
    resetResult();
    await toWorker("main", "clearTrade", "all");
  };

  // ── State: post-execution celebration ──────────────────────────────────────
  if (state.accepted) {
    return (
      <div data-v5-trade-content-first-screen="" data-trade-workbench="">
        <div className="trade-workbench-state" data-trade-state="result">
          <div className="alert alert-success" data-trade-result-accepted="">
            {/* The engine's proposeTrade message is English flavor text; render
                a translated line instead of leaking it into the zh UI. */}
            {i18n.t("trade.acceptedBanner", {
              ns: "ui",
              defaultValue: "Deal! The trade is done.",
            })}
            <div className="mt-2">
              <MobileP3EventFeedback event="tradeAccepted">
                {i18n.t("trade.p3AcceptedFeedback", {
                  ns: "ui",
                  defaultValue: "Trade accepted",
                })}
              </MobileP3EventFeedback>
            </div>
          </div>
          <div className="d-flex flex-column gap-2">
            <a className="btn btn-primary" href={helpers.leagueUrl(["roster"])}>
              {i18n.t("trade.viewRoster", { ns: "ui", defaultValue: "View roster" })}
            </a>
            <a className="btn btn-light-bordered" href={marketHomeHref}>
              {i18n.t("trade.returnToMarket", {
                ns: "ui",
                defaultValue: "Market home",
              })}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── State: trading closed (phase gate / challenge / spectator / game over) ──
  if (noTradingAllowed) {
    const rosterUrl = helpers.leagueUrl(["roster"]);
    const rosterAction = {
      href: rosterUrl,
      label: i18n.t("trade.closedActionRoster", {
        ns: "ui",
        defaultValue: "Review roster",
      }),
    };
    const dashboardAction = {
      href: helpers.leagueUrl([]),
      label: i18n.t("trade.closedActionLeagueDashboard", {
        ns: "ui",
        defaultValue: "Go to league dashboard",
      }),
      variant: "primary" as const,
    };
    const currentPhaseAction = {
      href: helpers.leagueUrl(getPhaseRoute(phase)),
      label: i18n.t("trade.closedActionCurrentPhase", {
        ns: "ui",
        defaultValue: "Go to current phase",
      }),
      variant: "primary" as const,
    };
    const draftPicksAction = {
      onClick: () => setDraftPicksSheetOpen(true),
      label: i18n.t("trade.closedActionDraftPicks", {
        ns: "ui",
        defaultValue: "Review draft assets",
      }),
    };
    const phaseClosedActions =
      currentPhaseAction.href === rosterUrl
        ? [currentPhaseAction, draftPicksAction]
        : [currentPhaseAction, rosterAction];

    const unavailable: {
      actions: {
        href?: string;
        onClick?: () => void;
        label: string;
        variant?: "primary" | "secondary";
      }[];
      body: string;
      title: string;
    } = (() => {
      if (challengeNoTrades) {
        return {
          actions: [{ ...rosterAction, variant: "primary" as const }],
          body: i18n.t("trade.unavailableChallengeBody", {
            ns: "ui",
            defaultValue:
              "Challenge Mode blocks trade proposals in this league. Review your roster and use the league's allowed roster-building paths instead.",
          }),
          title: i18n.t("trade.unavailableChallengeTitle", {
            ns: "ui",
            defaultValue: "Trades are disabled by Challenge Mode",
          }),
        };
      }
      if (spectator) {
        return {
          actions: [{ ...rosterAction, variant: "primary" as const }],
          body: i18n.t("trade.unavailableSpectatorBody", {
            ns: "ui",
            defaultValue:
              "The AI handles trades while you are in spectator mode. You can still review the roster, but trade actions stay locked.",
          }),
          title: i18n.t("trade.unavailableSpectatorTitle", {
            ns: "ui",
            defaultValue: "Trades are read-only in spectator mode",
          }),
        };
      }
      if (otherTeamsWantToHire) {
        return {
          actions: [dashboardAction, rosterAction],
          body: i18n.t("trade.unavailableJobOffersBody", {
            ns: "ui",
            defaultValue:
              "Resolve the open job offers before making trades. The league dashboard will take you back to the current decision path.",
          }),
          title: i18n.t("trade.unavailableJobOffersTitle", {
            ns: "ui",
            defaultValue: "Handle job offers before trading",
          }),
        };
      }
      if (gameOver) {
        return {
          actions: [dashboardAction, rosterAction],
          body: i18n.t("trade.unavailableGameOverBody", {
            ns: "ui",
            defaultValue:
              "This league is complete, so new trades are no longer available. You can still review the final roster and league history.",
          }),
          title: i18n.t("trade.unavailableGameOverTitle", {
            ns: "ui",
            defaultValue: "Trades are closed after game over",
          }),
        };
      }
      return {
        actions: phaseClosedActions,
        body:
          phase === PHASE.AFTER_TRADE_DEADLINE
            ? i18n.t("trade.closedAfterDeadlineBody", {
                ns: "ui",
                defaultValue:
                  "Trades are closed after the trade deadline. Continue with the current phase task or review your roster until trading reopens.",
              })
            : i18n.t("trade.closedBody", {
                ns: "ui",
                phase: PHASE_TEXT[phase],
                defaultValue:
                  "Trades are unavailable in the current phase. Current phase: {{phase}}. Continue with the current phase task or review your roster until trading reopens.",
              }),
        title: i18n.t("trade.closedTitle", {
          ns: "ui",
          defaultValue: "Trades are closed right now",
        }),
      };
    })();

    return (
      <div
        data-trade-closed-phase={phase}
        data-trade-workbench=""
        data-trade-state="closed"
      >
        <AvailabilityCard actions={unavailable.actions} title={unavailable.title}>
          {unavailable.body}
        </AvailabilityCard>
        <div className="mt-3">
          <a className="btn btn-light-bordered btn-sm" href={marketHomeHref}>
            {i18n.t("trade.returnToMarket", {
              ns: "ui",
              defaultValue: "Market home",
            })}
          </a>
        </div>
        <DraftPicksSheet
          onClose={() => setDraftPicksSheetOpen(false)}
          open={draftPicksSheetOpen}
          tid={props.userTid}
        />
      </div>
    );
  }

  // ── State: empty (no target picked yet) ─────────────────────────────────────
  if (targetPid === undefined) {
    const teamRecordText = formatSeasonRecord({ lost, otl, tied, won });
    const strategyLabel = i18n.t(`strategy.${strategy}`, {
      ns: "ui",
      defaultValue: strategy,
    });
    const recommendedOpponents = getRecommendedTradeOpponents({
      selectedTid: otherTid,
      teams,
    });
    return (
      <div data-v5-trade-content-first-screen="" data-trade-workbench="">
        <MobileContentHero
          className="mb-3"
          data-trade-state="empty"
          explainer={i18n.t("trade.emptyBody", {
            ns: "ui",
            defaultValue:
              "Find a player on another team you'd like to bring in, then tap “I want him.”",
          })}
        >
          <h2 className="h5 mb-3" data-trade-empty-title="">
            {i18n.t("trade.emptyTitle", {
              ns: "ui",
              defaultValue: "Pick a player you want first",
            })}
          </h2>
          <MobileStatRow
            className="mb-3"
            detail={teamRecordText}
            label={i18n.t("trade.rosterNeed", {
              ns: "ui",
              defaultValue: "Roster need",
            })}
            value={strategyLabel}
          />
          <div className="d-flex flex-column gap-2 mb-3">
            <a
              className="btn btn-primary"
              data-trade-browse-leaders=""
              href={createMobileCrossTabHref(helpers.leagueUrl(["leaders"]))}
            >
              {i18n.t("trade.emptyBrowseLeaders", {
                ns: "ui",
                defaultValue: "See the stat leaders",
              })}
            </a>
            <a
              className="btn btn-light-bordered"
              data-trade-browse-standings=""
              href={createMobileCrossTabHref(helpers.leagueUrl(["standings"]))}
            >
              {i18n.t("trade.emptyBrowseStandings", {
                ns: "ui",
                defaultValue: "Browse team rosters",
              })}
            </a>
          </div>
          {recommendedOpponents.length > 0 ? (
            <div data-trade-recommended-teams="">
              <div className="small text-body-secondary mb-1">
                {i18n.t("trade.emptyRecommendedTeams", {
                  ns: "ui",
                  defaultValue: "Teams that fit your needs",
                })}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {recommendedOpponents.map((t) => (
                  <span className="badge text-bg-secondary" key={t.tid}>
                    {`${translateTeamRegion(t.region)} ${translateTeamName(t.name)}`}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </MobileContentHero>
      </div>
    );
  }

  // ── State: workbench (quote + assembly) with optional result overlay ────────
  // askUnattainable: the engine reported NO package of tradable assets the AI
  // would take (quote.attainable === false). The quote card shows the honest
  // banner instead of an empty asking price, and auto-fill is disabled with a
  // reason (it would fail by the same math).
  const askUnattainable = quote !== null && !quote.attainable;
  const beam: Beam | undefined = preview?.beam ?? quote?.beam;
  const giveAssets = preview?.give ?? [];
  const payrollAfterTrade = summary.teams[0]?.payrollAfterTrade;
  const rosterDelta = 1 - userPids.length; // one player in (target) vs players out

  const includedPids = new Set(userPids);
  const includedDpids = new Set(userDpids);
  const addablePlayers = userRoster.filter((p) => !includedPids.has(p.pid));
  const addablePicks = userPicks.filter((dp) => !includedDpids.has(dp.dpid));

  const otherTeam = teams.find((t) => t.tid === otherTid);
  const otherTeamName = otherTeam
    ? `${translateTeamRegion(otherTeam.region)} ${translateTeamName(otherTeam.name)}`
    : i18n.t("trade.otherTeam", { ns: "ui", defaultValue: "Other team" });

  const renderResult = () => {
    if (!outcome) {
      return null;
    }
    if (outcome.outcome === "accept") {
      return (
        <div
          className="card border-success mb-3"
          data-trade-state="result"
          data-trade-result-accept=""
        >
          <div className="card-body">
            <h3 className="h6 text-success">
              {i18n.t("trade.resultAcceptTitle", {
                ns: "ui",
                defaultValue: "They accepted!",
              })}
            </h3>
            <p className="mb-3">
              {i18n.t("trade.resultAcceptBody", {
                ns: "ui",
                defaultValue: "Confirm to finish the trade.",
              })}
            </p>
            <div className="d-flex flex-column gap-2">
              <button
                className="btn btn-success"
                data-trade-confirm-execute=""
                disabled={busy}
                onClick={() => {
                  void executeTrade();
                }}
                type="button"
              >
                {i18n.t("trade.confirmExecute", {
                  ns: "ui",
                  defaultValue: "Confirm the trade",
                })}
              </button>
              <button
                className="btn btn-light-bordered"
                disabled={busy}
                onClick={resetResult}
                type="button"
              >
                {i18n.t("trade.reviseOffer", {
                  ns: "ui",
                  defaultValue: "Change my offer",
                })}
              </button>
            </div>
          </div>
        </div>
      );
    }
    if (outcome.outcome === "counter") {
      return (
        <div
          className="card border-warning mb-3"
          data-trade-state="result"
          data-trade-result-counter=""
        >
          <div className="card-body">
            <h3 className="h6 text-warning-emphasis">
              {i18n.t("trade.resultCounterTitle", {
                ns: "ui",
                defaultValue: "They countered",
              })}
            </h3>
            <p className="fst-italic mb-2" data-trade-gm-line="">
              {toneLine(outcome.counter.toneLevel)}
            </p>
            <p className="small mb-1">
              {i18n.t("trade.resultCounterBody", {
                ns: "ui",
                defaultValue: "They want you to add:",
              })}
            </p>
            <div
              className="mb-2"
              data-trade-counter-add-give-pids={JSON.stringify(
                outcome.counter.addGive
                  .filter((asset) => asset.type === "player")
                  .map((asset) => asset.pid),
              )}
              data-trade-counter-diff=""
            >
              {outcome.counter.addGive.map((asset) => (
                <AssetRow
                  asset={asset}
                  counterAdd
                  key={asset.type === "player" ? `p${asset.pid}` : `d${asset.dpid}`}
                />
              ))}
            </div>
            <div
              className="small text-body-secondary mb-3"
              data-trade-counter-patience-left={outcome.counter.patienceLeft}
            >
              {i18n.t("trade.patienceLeft", {
                ns: "ui",
                count: outcome.counter.patienceLeft,
                defaultValue: "Patience left: {{count}}",
              })}
            </div>
            <div className="d-flex flex-column gap-2">
              <button
                className="btn btn-success"
                data-trade-accept-counter=""
                disabled={busy}
                onClick={acceptCounter}
                type="button"
              >
                {i18n.t("trade.acceptCounter", {
                  ns: "ui",
                  defaultValue: "Take their counter",
                })}
              </button>
              <button
                className="btn btn-light-bordered"
                disabled={busy}
                onClick={resetResult}
                type="button"
              >
                {i18n.t("trade.reviseOffer", {
                  ns: "ui",
                  defaultValue: "Change my offer",
                })}
              </button>
              <button
                className="btn btn-link text-body-secondary"
                disabled={busy}
                onClick={walkAway}
                type="button"
              >
                {i18n.t("trade.walkAway", { ns: "ui", defaultValue: "Never mind" })}
              </button>
            </div>
          </div>
        </div>
      );
    }
    // reject
    const reasonText =
      outcome.reasonHint === "salary_match"
        ? i18n.t("trade.rejectSalaryMatch", {
            ns: "ui",
            defaultValue:
              "This package breaks salary-matching rules. Adjust the salaries or assets and try again.",
          })
        : outcome.reasonHint === "hard_cap"
          ? i18n.t("trade.rejectHardCap", {
              ns: "ui",
              defaultValue:
                "This package would put a team over the hard cap. Adjust the salaries or assets and try again.",
            })
          : outcome.reasonHint === "cooldown"
            ? i18n.t("trade.rejectCooldown", {
                ns: "ui",
                defaultValue: "We're not talking trades right now — come back later.",
              })
            : outcome.reasonHint === "too_lopsided"
              ? i18n.t("trade.rejectTooLopsided", {
                  ns: "ui",
                  defaultValue: "This is way too lopsided for us.",
                })
              : i18n.t("trade.rejectExhausted", {
                  ns: "ui",
                  defaultValue: "We're done talking about this one.",
                });
    return (
      <div
        className="card border-danger mb-3"
        data-trade-state="result"
        data-trade-result-reject=""
        data-trade-result-reason={outcome.reasonHint}
      >
        <div className="card-body">
          <h3 className="h6 text-danger">
            {i18n.t("trade.resultRejectTitle", {
              ns: "ui",
              defaultValue: "They said no",
            })}
          </h3>
          <p className="fst-italic mb-2" data-trade-gm-line="">
            {reasonText}
          </p>
          {"worse" in outcome && outcome.worse ? (
            <p className="small text-danger mb-2">
              {i18n.t("trade.worseWarning", {
                ns: "ui",
                defaultValue: "Lowering your offer only made them angrier.",
              })}
            </p>
          ) : null}
          <div className="d-flex flex-column gap-2">
            <button
              className="btn btn-light-bordered"
              disabled={busy || outcome.reasonHint === "cooldown"}
              onClick={resetResult}
              type="button"
            >
              {i18n.t("trade.reviseOffer", {
                ns: "ui",
                defaultValue: "Change my offer",
              })}
            </button>
            <button
              className="btn btn-link text-body-secondary"
              disabled={busy}
              onClick={walkAway}
              type="button"
            >
              {i18n.t("trade.walkAway", { ns: "ui", defaultValue: "Never mind" })}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-v5-trade-content-first-screen="" data-trade-workbench="">
      <MobileContentHero
        className="mb-3"
        explainer={
          quote
            ? quote.attainable
              ? gmAskLine(quote.target.name, quote.hint)
              : gmUnattainableLine(quote.target.name)
            : i18n.t("trade.loadingQuote", {
                ns: "ui",
                defaultValue: "Getting their asking price…",
              })
        }
      >
        {/* Quote: target + the AI's asking price */}
        <div className="trade-quote-card" data-trade-state="quote" data-trade-ask="">
          {quote ? (
            <>
              <div className="mb-2" data-trade-target="" data-trade-target-pid={quote.target.pid}>
                <div className="h5 mb-0">
                  {i18n.t("trade.targetHeading", {
                    ns: "ui",
                    name: quote.target.name,
                    defaultValue: "You want {{name}}",
                  })}
                </div>
                <div className="small text-body-secondary">
                  {i18n.t("trade.targetMetaWithoutRating", {
                    ns: "ui",
                    pos: quote.target.pos,
                    age: quote.target.age,
                    defaultValue: "{{pos}} · age {{age}}",
                  })}
                </div>
                <MarketRatingStatusMetric
                  className="mt-2"
                  hidden={challengeNoRatings}
                  label={i18n.t("marketMetrics.playerLevel", {
                    ns: "ui",
                    defaultValue: "Player level",
                  })}
                  rating={quote.target.ovr}
                />
              </div>
              {quote.attainable ? (
                <>
                  <div className="small fw-semibold mb-1">
                    {i18n.t("trade.askHeading", {
                      ns: "ui",
                      team: otherTeamName,
                      defaultValue: "What {{team}} wants",
                    })}
                  </div>
                  <div className="mb-2">
                    {quote.ask.give.map((asset) => (
                      <AssetRow
                        asset={asset}
                        key={asset.type === "player" ? `p${asset.pid}` : `d${asset.dpid}`}
                      />
                    ))}
                  </div>
                </>
              ) : (
                // Honest unattainable state: no zero-asset "asking price" —
                // the engine found no package the AI would take.
                <div className="alert alert-warning mb-2" data-trade-unattainable="">
                  <div className="fw-semibold">
                    {i18n.t("trade.unattainableTitle", {
                      ns: "ui",
                      defaultValue: "They won't let him go",
                    })}
                  </div>
                  <div className="small">
                    {i18n.t("trade.unattainableBody", {
                      ns: "ui",
                      defaultValue:
                        "No package from your current tradable assets would get them to yes. Try another target, or come back when your chips are stronger.",
                    })}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </MobileContentHero>

      {executionError ? (
        <div
          className="alert alert-danger mb-3"
          data-trade-execution-error={executionError}
          role="alert"
        >
          {executionErrorText(executionError)}
        </div>
      ) : null}

      {outcome ? (
        renderResult()
      ) : (
        <>
          {/* Live value balance beam */}
          {beam ? <BalanceBeam beam={beam} /> : null}

          {/* Assembly: your package builder */}
          <section
            className="trade-flow-panel mb-3"
            data-asset-workspace=""
            data-trade-state="assembly"
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="h6 mb-0">
                {i18n.t("trade.yourOffer", { ns: "ui", defaultValue: "Your offer" })}
              </h3>
              <button
                className="btn btn-sm btn-primary"
                data-trade-autofill=""
                disabled={busy || askUnattainable}
                onClick={handleAutoFill}
                type="button"
              >
                {i18n.t("trade.autoFill", {
                  ns: "ui",
                  defaultValue: "Auto-fill the chips",
                })}
              </button>
            </div>
            {askUnattainable ? (
              <p className="small text-body-secondary mb-2" data-trade-autofill-unattainable="">
                {i18n.t("trade.autoFillUnattainableReason", {
                  ns: "ui",
                  defaultValue: "Auto-fill can't reach their price with your current assets",
                })}
              </p>
            ) : null}
            {giveAssets.length > 0 ? (
              <div className="mb-2">
                {giveAssets.map((asset) => (
                  <AssetRow
                    asset={asset}
                    key={asset.type === "player" ? `p${asset.pid}` : `d${asset.dpid}`}
                    onRemove={() =>
                      handleToggleGive(
                        asset.type === "player" ? "player" : "pick",
                        (asset.type === "player" ? asset.pid : asset.dpid)!,
                      )
                    }
                    selected
                  />
                ))}
              </div>
            ) : (
              <p className="text-body-secondary small mb-2">
                {i18n.t("trade.offerEmpty", {
                  ns: "ui",
                  defaultValue: "No chips in yet",
                })}
              </p>
            )}

            <details className="mb-2" data-native-details-host="trade-add-players">
              <summary className="btn btn-light-bordered btn-sm">
                {i18n.t("trade.addPlayers", {
                  ns: "ui",
                  defaultValue: "Add your players",
                })}
              </summary>
              <div className="mt-2">
                {addablePlayers.map((p) => {
                  const untradableMsg = translateUntradableMessage(p.untradableMsg);
                  return (
                    <div
                      className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom"
                      data-trade-add-player-row-pid={p.pid}
                      key={p.pid}
                    >
                      <div className="min-width-0">
                        <div className="fw-semibold text-truncate">
                          <PlayerNameLabels
                            pid={p.pid}
                            injury={p.injury}
                            jerseyNumber={p.jerseyNumber}
                            skills={p.ratings.skills}
                            defaultWatch={p.watch}
                            firstName={p.firstName}
                            firstNameShort={p.firstNameShort}
                            lastName={p.lastName}
                            nameOnly
                          />
                        </div>
                        <div className="small text-body-secondary">
                          <PositionText pos={p.ratings.pos} />
                          {!challengeNoRatings
                            ? ` · ${i18n.t("trade.ovrShort", {
                                ns: "ui",
                                ovr: p.ratings.ovr,
                                defaultValue: "{{ovr}} overall",
                              })}`
                            : ""}
                          {untradableMsg ? (
                            <span className="text-danger"> · {untradableMsg}</span>
                          ) : null}
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-primary"
                        data-trade-add-player-pid={p.pid}
                        disabled={p.untradable || busy}
                        onClick={() => handleToggleGive("player", p.pid)}
                        type="button"
                      >
                        {i18n.t("trade.addAssetAction", {
                          ns: "ui",
                          defaultValue: "Add",
                        })}
                      </button>
                    </div>
                  );
                })}
              </div>
            </details>

            {addablePicks.length > 0 ? (
              <details className="mb-2" data-native-details-host="trade-add-picks">
                <summary className="btn btn-light-bordered btn-sm">
                  {i18n.t("trade.addPicks", {
                    ns: "ui",
                    defaultValue: "Add your picks",
                  })}
                </summary>
                <div className="mt-2">
                  {addablePicks.map((dp) => (
                    <div
                      className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom"
                      key={dp.dpid}
                    >
                      <span className="min-width-0 text-truncate">{dp.desc}</span>
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={busy}
                        onClick={() => handleToggleGive("pick", dp.dpid)}
                        type="button"
                      >
                        {i18n.t("trade.addAssetAction", {
                          ns: "ui",
                          defaultValue: "Add",
                        })}
                      </button>
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
          </section>

          {/* Consequence line: cap space / roster count after the deal */}
          <div
            className="trade-flow-summary-strip bg-body border rounded p-2 mb-3"
            data-trade-impact-summary="TradeImpactSummary"
          >
            <div className="row g-2 small">
              <div className="col-6">
                <div className="text-body-secondary">
                  {i18n.t("trade.consequencePayroll", {
                    ns: "ui",
                    defaultValue: "Payroll after",
                  })}
                </div>
                <div className="fw-semibold">
                  {payrollAfterTrade !== undefined
                    ? helpers.formatCurrency(payrollAfterTrade, "M")
                    : i18n.t("trade.notAvailable", {
                        ns: "ui",
                        defaultValue: "Not available",
                      })}
                </div>
              </div>
              <div className="col-6">
                <div className="text-body-secondary">
                  {i18n.t("trade.consequenceRoster", {
                    ns: "ui",
                    defaultValue: "Roster size change",
                  })}
                </div>
                <div className="fw-semibold">
                  {rosterDelta > 0 ? `+${rosterDelta}` : rosterDelta}
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div
            className="mobile-primary-cta trade-submit-action d-md-block"
            data-desktop-trade-submit-action=""
          >
            <button
              className="btn btn-primary mobile-primary-cta-button w-100"
              data-trade-submit=""
              disabled={busy || userPids.length + userDpids.length === 0}
              onClick={handleSubmit}
              type="button"
            >
              {busy
                ? i18n.t("trade.submitting", {
                    ns: "ui",
                    defaultValue: "Sending it over…",
                  })
                : i18n.t("trade.submitOffer", {
                    ns: "ui",
                    defaultValue: "Submit this offer",
                  })}
            </button>
          </div>
        </>
      )}

      <div className="mt-3">
        <a className="btn btn-link btn-sm px-0" href={marketHomeHref}>
          {i18n.t("trade.returnToMarket", { ns: "ui", defaultValue: "Market home" })}
        </a>
      </div>
    </div>
  );
};

export default Trade;
